import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Card } from '@/components/Card';
import { Page } from '@/components/layout/Page';
import { RouterConfig, CanvasRoutes } from '@/configs/routes';

type StatusKey = 'complete' | 'review' | 'gaps';
type ColorKey = 'positive' | 'warning' | 'negative' | 'default' | 'blue-dark' | 'blue-mid' | 'muted';

type HomeData = {
  businessObjects: {
    total: string;
    items: { name: string; source: string; count: string; status: StatusKey }[];
  };
  orderToCash: {
    total: string;
    stages: { name: string; count: string; highlight?: boolean }[];
    metrics: { label: string; value: string; color: ColorKey }[];
  };
  patternsLearned: {
    total: string;
    groups: { label: string; color: ColorKey; items: { name: string; conf: string; color: ColorKey }[] }[];
  };
  dataQuality: {
    score: string;
    assessed: string;
    sourceSystems: { name: string; desc: string; score: string; color: ColorKey }[];
    metrics: { value: string; label: string; color: ColorKey }[];
  };
  aiReadiness: {
    score: string;
    assessed: string;
    dimensions: { name: string; pct: number; color: ColorKey; detail: string }[];
    callout: { title: string; body: string };
  };
  reviewRequired: {
    totalLabel: string;
    items: { name: string; count: string; dot: ColorKey }[];
  };
};

const TEXT_COLOR: Record<ColorKey, string> = {
  positive: '#2A8055',
  warning: '#D27A24',
  negative: '#BC2E3D',
  default: '#1A1C2E',
  'blue-dark': '#274483',
  'blue-mid': '#4A6BAD',
  muted: '#9295A5',
};

const STATUS_CONFIG: Record<StatusKey, { bg: string; color: string; label: string }> = {
  complete: { bg: 'rgba(42,128,85,.1)', color: '#2A8055', label: '● Complete' },
  review: { bg: 'rgba(210,122,36,.12)', color: '#D27A24', label: '◑ Review' },
  gaps: { bg: 'rgba(188,46,61,.1)', color: '#BC2E3D', label: '○ Gaps' },
};

function CardLink({ to, label }: { to?: string; label: string }) {
  const cls = 'text-[11px] font-semibold text-[#274483] cursor-pointer';
  if (to) {
    return (
      <Link to={to} className={cls}>
        {label}
      </Link>
    );
  }
  return <span className={cls}>{label}</span>;
}

export function DataHome() {
  const { data } = useQuery<HomeData>({
    queryKey: ['data-app', 'home'],
    queryFn: () => fetch('/api/data-app/home').then((r) => r.json()),
  });

  const bo = data?.businessObjects;
  const o2c = data?.orderToCash;
  const pat = data?.patternsLearned;
  const dq = data?.dataQuality;
  const ai = data?.aiReadiness;
  const rev = data?.reviewRequired;

  return (
    <Page
      title="Your Supply Chain Data"
      subheader="Keystone has ingested and reconstructed your ERP data across SAP SD, MM, and PP — mapping your business objects, rebuilding order-to-cash events, and learning the patterns of how your supply chain behaves."
    >
      <div className="space-y-3.5">
        {/* Row 1: four cards */}
        <div className="grid grid-cols-4 gap-3.5">
          {/* Card 1: Business Objects Mapped */}
          <Card className="p-0 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[#E4E0D8] flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold text-[#1A1C2E]">Business Objects Mapped</span>
              <span className="text-[10px] text-[#9295A5]">Source: SAP SD · MM · PP</span>
            </div>
            <div className="px-4 py-2.5 flex-1 flex flex-col">
              <p className="text-[26px] font-bold text-[#1A1C2E] tracking-[-0.03em] mb-3">
                {bo?.total ?? '—'} <span className="text-[12px] font-normal text-[#9295A5]">objects</span>
              </p>
              <div className="grid grid-cols-[1fr_auto_auto] mb-1 pb-1 border-b border-[#E4E0D8]">
                <span className="text-[8.5px] font-bold uppercase tracking-[.07em] text-[#9295A5]">Object</span>
                <span className="text-[8.5px] font-bold uppercase tracking-[.07em] text-[#9295A5] text-right pr-2.5">
                  Count
                </span>
                <span className="text-[8.5px] font-bold uppercase tracking-[.07em] text-[#9295A5] text-right">
                  Quality
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {(bo?.items ?? []).map((obj) => {
                  const sc = STATUS_CONFIG[obj.status];
                  return (
                    <div key={obj.name} className="grid grid-cols-[1fr_auto_auto] items-center">
                      <div>
                        <div className="text-[11px] text-[#1A1C2E] font-medium">{obj.name}</div>
                        <div className="text-[9.5px] text-[#9295A5]">{obj.source}</div>
                      </div>
                      <span className="text-[11px] font-bold text-[#1A1C2E] pr-2.5 text-right">{obj.count}</span>
                      <span
                        className="text-[8.5px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: sc.bg, color: sc.color }}
                      >
                        {sc.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2.5 pt-2 border-t border-[#E4E0D8] flex items-center gap-4">
                <CardLink to={RouterConfig[CanvasRoutes.DataCustomers].path} label="→ View Customers" />
                <CardLink to={RouterConfig[CanvasRoutes.DataProducts].path} label="→ View Products" />
              </div>
            </div>
          </Card>

          {/* Card 2: Order to Cash Events */}
          <Card className="p-0 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[#E4E0D8] flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold text-[#1A1C2E]">Order to Cash Events</span>
              <span className="text-[10px] text-[#9295A5]">Source: SAP SD · CDHDR/CDPOS</span>
            </div>
            <div className="px-4 py-2.5 flex-1 flex flex-col">
              <p className="text-[26px] font-bold text-[#1A1C2E] tracking-[-0.03em] mb-3">
                {o2c?.total ?? '—'} <span className="text-[12px] font-normal text-[#9295A5]">events reconstructed</span>
              </p>
              {/* Stage flow */}
              <div className="flex items-center mb-3 overflow-hidden">
                {(o2c?.stages ?? []).map((stage, i) => (
                  <div key={i} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className="text-[9px] font-bold text-center leading-tight whitespace-pre-line"
                        style={{ color: stage.highlight ? '#2A8055' : '#274483' }}
                      >
                        {stage.name}
                      </div>
                      <div className="text-[9px] text-[#9295A5] mt-0.5">{stage.count}</div>
                    </div>
                    {i < (o2c?.stages.length ?? 0) - 1 && <div className="w-3 h-px bg-[#E4E0D8] flex-shrink-0" />}
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                {(o2c?.metrics ?? []).map((m) => (
                  <div key={m.label} className="flex items-center justify-between">
                    <span className="text-[11px] text-[#5C5F72]">{m.label}</span>
                    <span className="text-[11px] font-bold" style={{ color: TEXT_COLOR[m.color] }}>
                      {m.value}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2.5 pt-2 border-t border-[#E4E0D8]">
                <CardLink to={RouterConfig[CanvasRoutes.DataEvents].path} label="→ View Events" />
              </div>
            </div>
          </Card>

          {/* Card 3: Patterns Learned */}
          <Card className="p-0 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[#E4E0D8] flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold text-[#1A1C2E]">Patterns Learned</span>
              <span className="text-[10px] text-[#9295A5]">Behaviours · Effects · Anomalies</span>
            </div>
            <div className="px-4 py-2.5 flex-1 flex flex-col">
              <p className="text-[26px] font-bold text-[#1A1C2E] tracking-[-0.03em] mb-3">
                {pat?.total ?? '—'} <span className="text-[12px] font-normal text-[#9295A5]">patterns active</span>
              </p>
              {(pat?.groups ?? []).map((group, gi) => (
                <div key={group.label} className={gi < (pat?.groups.length ?? 0) - 1 ? 'mb-2.5' : ''}>
                  <div
                    className="text-[9px] font-bold uppercase tracking-[.08em] mb-1.5"
                    style={{ color: TEXT_COLOR[group.color] }}
                  >
                    {group.label}
                  </div>
                  <div className="flex flex-col gap-1">
                    {group.items.map((it) => (
                      <div key={it.name} className="flex items-center justify-between">
                        <span className="text-[11px] text-[#5C5F72]">{it.name}</span>
                        <span className="text-[10.5px] font-bold" style={{ color: TEXT_COLOR[it.color] }}>
                          {it.conf}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mt-2.5 pt-2 border-t border-[#E4E0D8]">
                <CardLink label="→ View Patterns" />
              </div>
            </div>
          </Card>

          {/* Card 4: Data Quality */}
          <Card className="p-0 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[#E4E0D8]">
              <div className="flex items-baseline gap-2.5">
                <span className="text-[11px] font-semibold text-[#1A1C2E]">Data Quality</span>
                <span className="text-[22px] font-bold text-[#2A8055] tracking-[-0.02em]">{dq?.score ?? '—'}</span>
                <span className="text-[10px] font-bold text-[#2A8055]">● AI-ready</span>
              </div>
              <div className="text-[10px] text-[#9295A5] mt-0.5">{dq?.assessed}</div>
            </div>
            <div className="px-4 py-2.5 flex-1 flex flex-col">
              {/* Source systems */}
              <div className="flex flex-col gap-1.5 mb-3">
                <div className="text-[9px] font-bold uppercase tracking-[.08em] text-[#9295A5] mb-0.5">
                  Source Systems
                </div>
                {(dq?.sourceSystems ?? []).map((s) => (
                  <div
                    key={s.name}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded"
                    style={{
                      background: s.color === 'warning' ? 'rgba(210,122,36,.05)' : 'rgba(42,128,85,.05)',
                      borderLeft: `3px solid ${TEXT_COLOR[s.color]}`,
                    }}
                  >
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: TEXT_COLOR[s.color] }} />
                    <div className="flex-1">
                      <div className="text-[11px] font-bold text-[#1A1C2E]">{s.name}</div>
                      <div className="text-[9.5px] text-[#9295A5]">{s.desc}</div>
                    </div>
                    <span className="text-[11px] font-bold" style={{ color: TEXT_COLOR[s.color] }}>
                      {s.score}
                    </span>
                  </div>
                ))}
              </div>
              {/* Key metrics */}
              <div className="grid grid-cols-2 gap-2 mb-2.5">
                {(dq?.metrics ?? []).map((m) => (
                  <div
                    key={m.label}
                    className="px-2.5 py-2 rounded text-center"
                    style={{ background: m.color === 'warning' ? 'rgba(210,122,36,.06)' : '#F5F6F8' }}
                  >
                    <div className="text-[16px] font-bold" style={{ color: TEXT_COLOR[m.color] }}>
                      {m.value}
                    </div>
                    <div className="text-[9.5px] text-[#9295A5] mt-px">{m.label}</div>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-[#E4E0D8]">
                <CardLink to={RouterConfig[CanvasRoutes.DataQuality].path} label="→ View Data Quality" />
              </div>
            </div>
          </Card>
        </div>

        {/* Row 2: AI Readiness (span 3) + Review Required */}
        <div className="grid grid-cols-4 gap-3.5">
          {/* AI Readiness */}
          <Card className="p-0 overflow-hidden col-span-3">
            <div className="px-4 py-3 border-b border-[#E4E0D8]">
              <div className="flex items-baseline gap-3">
                <span className="text-[11px] font-semibold text-[#1A1C2E]">AI Readiness</span>
                <span className="text-[28px] font-bold text-[#274483] tracking-[-0.03em]">{ai?.score ?? '—'}</span>
                <span className="text-[11px] font-semibold text-[#2A8055]">● Ready</span>
              </div>
              <div className="text-[10px] text-[#9295A5] mt-0.5">{ai?.assessed}</div>
            </div>
            <div className="px-4 py-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                {(ai?.dimensions ?? []).map((d) => (
                  <div key={d.name}>
                    <div className="flex justify-between mb-1">
                      <span className="text-[11px] text-[#5C5F72]">{d.name}</span>
                      <span className="text-[11px] font-bold" style={{ color: TEXT_COLOR[d.color] }}>
                        {d.pct}%
                      </span>
                    </div>
                    <div className="h-1 rounded-sm bg-[#E4E0D8]">
                      <div className="h-1 rounded-sm" style={{ width: `${d.pct}%`, background: TEXT_COLOR[d.color] }} />
                    </div>
                    <div className="text-[9.5px] text-[#9295A5] mt-0.5">{d.detail}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2.5 px-3 py-2 rounded bg-[rgba(39,68,131,.05)] border-l-[3px] border-[#274483]">
                <div className="text-[11px] text-[#274483] font-semibold">{ai?.callout.title}</div>
                <div className="text-[10.5px] text-[#5C5F72] mt-0.5">{ai?.callout.body}</div>
              </div>
            </div>
          </Card>

          {/* Review Required */}
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E4E0D8] flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold text-[#1A1C2E]">Review Required</span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[rgba(188,46,61,0.1)] text-[#BC2E3D]">
                {rev?.totalLabel}
              </span>
            </div>
            <div className="px-4 py-2.5 flex flex-col gap-2">
              {(rev?.items ?? []).map((it, i) => (
                <div
                  key={it.name}
                  className="flex items-center gap-2 py-1.5"
                  style={{ borderBottom: i < (rev?.items.length ?? 0) - 1 ? '1px solid #E4E0D8' : 'none' }}
                >
                  <div
                    className="w-[7px] h-[7px] rounded-full flex-shrink-0"
                    style={{ background: TEXT_COLOR[it.dot] }}
                  />
                  <span className="text-[11.5px] text-[#5C5F72] flex-1">{it.name}</span>
                  <span className="text-[11px] font-bold text-[#1A1C2E]">{it.count}</span>
                </div>
              ))}
              <div className="mt-1 pt-2.5 border-t border-[#E4E0D8]">
                <CardLink label="→ Open Review Queue" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}
